import { useRef, useState } from 'react';
import { Image, Loader2, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';

const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

function getExtension(mime: string): string {
  const map: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
  };
  return map[mime] ?? 'jpg';
}

interface Props {
  label: string;
  value?: string | null;
  onChange: (url: string) => void;
  bucket?: string;
  folder?: string;
  slug?: string;
  recommended?: string;
  aspectHint?: string;
  accept?: string;
  disabled?: boolean;
  previewClass?: string;
}

export default function ImageUploadField({
  label,
  value,
  onChange,
  bucket = 'parcours-assets',
  folder = 'uploads',
  slug,
  recommended,
  accept = 'image/jpeg,image/png,image/webp,image/svg+xml',
  disabled = false,
  previewClass,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  async function uploadFile(file: File) {
    if (!ALLOWED_MIME.includes(file.type)) {
      toast.error(`Format non supporté. Utilisez JPG, PNG, WebP ou SVG.`);
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Fichier trop lourd (max 10 Mo).`);
      return;
    }

    const ext = getExtension(file.type);
    const base = slug ? `${slug}-${Date.now()}` : `${Date.now()}`;
    const path = `${folder}/${base}.${ext}`;

    setUploading(true);
    try {
      const { error } = await supabase.storage
        .from(bucket)
        .upload(path, file, { upsert: true, contentType: file.type });

      if (error) {
        toast.error(`Erreur upload : ${error.message}`);
        return;
      }

      const { data } = supabase.storage.from(bucket).getPublicUrl(path);
      onChange(data.publicUrl);
      toast.success('Image uploadée.');
    } catch (e) {
      toast.error('Erreur inattendue lors de l\'upload.');
      console.error(e);
    } finally {
      setUploading(false);
    }
  }

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    uploadFile(files[0]);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    if (disabled || uploading) return;
    handleFiles(e.dataTransfer.files);
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (!disabled && !uploading) setDragging(true);
  }

  function handleDragLeave() {
    setDragging(false);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <label className="block text-sm font-medium text-slate-700">
          {label}
          {recommended && (
            <span className="ml-1.5 text-xs font-normal text-slate-400">{recommended}</span>
          )}
        </label>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            disabled={disabled || uploading}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 transition-colors disabled:opacity-40"
          >
            <X className="h-3 w-3" /> Supprimer
          </button>
        )}
      </div>

      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && !uploading && inputRef.current?.click()}
        className={[
          'relative flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-5 text-center transition-colors',
          dragging
            ? 'border-pink-400 bg-pink-50'
            : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white',
          disabled || uploading ? 'pointer-events-none opacity-60' : '',
        ].join(' ')}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="sr-only"
          onChange={(e) => handleFiles(e.target.files)}
          disabled={disabled || uploading}
        />

        {uploading ? (
          <>
            <Loader2 className="h-7 w-7 animate-spin text-pink-500" />
            <p className="text-xs text-slate-500">Upload en cours...</p>
          </>
        ) : (
          <>
            <Upload className="h-7 w-7 text-slate-400" />
            <p className="text-xs font-medium text-slate-600">
              Glissez une image ici ou{' '}
              <span className="text-pink-600 underline underline-offset-2">choisissez un fichier</span>
            </p>
            <p className="text-xs text-slate-400">JPG, PNG, WebP, SVG — max 10 Mo</p>
          </>
        )}
      </div>

      {/* Preview */}
      {value && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          <img
            src={value}
            alt="Aperçu"
            className={previewClass ?? 'h-24 w-full object-cover'}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </div>
      )}

      {/* URL manuelle */}
      <div className="flex items-center gap-2">
        <Image className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <input
          type="url"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Ou collez une URL directement..."
          disabled={disabled || uploading}
          className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 placeholder-slate-400 outline-none transition focus:border-slate-400 focus:ring-1 focus:ring-slate-300 disabled:opacity-50"
        />
      </div>
    </div>
  );
}
